import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import AppRoutes from 'constants/AppRoutes';
import { SEARCH_PARAMS } from 'constants/dataSets';
import { SHOW_ARCHIVED } from 'constants/formFields';
import { USER_PILLAR_MODULES } from 'constants/interfaces';
import useDataSetListActions from 'hooks/useDataSetListActions';
import useDownloadExample from 'hooks/useDownloadExample';
import useImportErrorModal from 'hooks/useImportErrorModal';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import {
  useGetProductCategoriesQuery,
  useLazyProductExampleQuery,
  useProductArchiveMutation,
  useProductDeArchiveMutation,
  useProductImportRecordsMutation,
  useProductListQuery,
  useProductRecordDeleteMutation,
  useProfitCenterListQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';

import useDataSetsListForm from '../ProfitCentres/hooks/useDataSetsListForm';
import ProductsView from './ProductsView';

import type IProductItem from 'store/slices/dataSets/interfaces/IProductItem';
import type IArchiveError from '../ProfitCentres/interfaces/IArchiveError';
import type { TUseProductImportRecordsMutation } from './interfaces/TUseProductImportRecordsMutation';

function Products() {
  const navigate = useNavigate();
  const {
    isActionsVisible,
    isLoading: isPillarModuleLoading,
  } = usePillarModulePermission(
    { pillarModuleKey: USER_PILLAR_MODULES.settings.dataSetsManagement },
  );

  const [getProductsheetExample, { isLoading: isLazyProductExampleLoading }] = useLazyProductExampleQuery();
  const {
    control, getValues, setValue, watch,
  } = useDataSetsListForm();
  const [orderBy, setOrderBy] = useState('');
  const [searchParams] = useSearchParams();
  const [hasTriedArchived, setHasTriedArchived] = useState(false);

  const isArchived = watch(SHOW_ARCHIVED);
  const searchParam = searchParams.get(SEARCH_PARAMS.search) || '';
  const offset = Number(searchParams.get(SEARCH_PARAMS.offset) || 0);

  useEffect(() => {
    const isArchivedParam = searchParams.get(SEARCH_PARAMS.isArchived);
    const isArchivedFromUrl = isArchivedParam !== null ? Boolean(isArchivedParam === 'true') : false;

    if (isArchivedFromUrl !== isArchived) {
      setValue(SHOW_ARCHIVED, isArchivedFromUrl);
    }
  }, [searchParams, isArchived, setValue]);

  const { data: productCategories, isLoading: isProductCategoriesLoading } = useGetProductCategoriesQuery();
  const productCategoriesOptions = productCategories?.data?.items.map((category) => ({
    label: category.name,
    value: `${category.id}`,
  }));

  const { data, isFetching: isProductListLoading } = useProductListQuery({
    orderBy, keyword: searchParam, archived: isArchived, offset,
  });

  const [importRecords, { isLoading: isProductImportRecordsLoading }] = useProductImportRecordsMutation();

  const {
    isImportErrorModalVisible,
    importErrorMessage,
    handleImportError,
    handleCloseImportErrorModal,
  } = useImportErrorModal();

  const {
    uploadControl, isMappingModalVisible, onCloseMappingModal, isImportErrorsModalVisible,
    setIsImportErrorsModalVisible,
  } = useDownloadExample<TUseProductImportRecordsMutation>({
    importRecords,
    onError: handleImportError,
  });

  const { items } = data?.data || {};

  const [deArchive, { isLoading: isProductDeArchiveLoading, error: deArchiveError }] = useProductDeArchiveMutation();

  const [archive, { isLoading: isProductArchiveLoading, error: archiveError }] = useProductArchiveMutation();

  const [deleteRecord, {
    isLoading: isProductDeleteLoading,
    error: deleteError,
  }] = useProductRecordDeleteMutation();

  const {
    isArchiveModalVisible,
    isArchiveModalInfoVisible,
    latestArchivation,
    isLineMark,
    onSortChange,
    onSearchChange,
    onSelectAll,
    onArchiveConfirm,
    onArchive,
    onArchiveModalInfoOpen,
    onMoveToArchive,
    onDeArchive,
    onPageChange,
    isArchiveSelectedItemsVisible,
    setIsArchiveModalVisible,
    setIsArchiveModalInfoVisible,
    selectedItems,
    onShowArchiveChange,
    onOpenRestrictedModal,
    activeRestrictedModal,
    onDeleteRecord,
    isDeleteModalVisible,
    onCloseDeleteModal,
    onDeleteConfirm,
  } = useDataSetListActions<IProductItem[]>({
    items,
    archiveAction: archive,
    deArchiveAction: deArchive,
    deleteAction: deleteRecord,
    setOrderBy,
    getValues,
    setValue,
    watch,
  });

  const { data: profitCenterData, isLoading: isProfitCenterListLoading } = useProfitCenterListQuery({});

  const isWithSearch = !!searchParam;

  const isProfitCenterEmpty = !profitCenterData?.data?.meta?.count && isActionsVisible;

  const isUploadFileVisible = isActionsVisible && !isProfitCenterEmpty;

  const isEmptyList = !data?.data?.meta?.count || data?.data?.items?.length === 0;

  const currentTotalItems = items?.length || 0;

  const onDownloadExample = () => {
    getProductsheetExample();
  };

  const onEditRecord = (recordId: number) => {
    navigate(generatePath(AppRoutes.productsEdit, { recordId: String(recordId) }));
  };

  const onAddRecord = () => {
    navigate(AppRoutes.productsCreate);
  };

  const meta = data?.data?.meta;

  const isLoading = isProductListLoading || isProductImportRecordsLoading || isProductDeArchiveLoading
      || isProductArchiveLoading || isProductDeleteLoading || isLazyProductExampleLoading
      || isProfitCenterListLoading || isPillarModuleLoading || isProductCategoriesLoading;

  useEffect(() => {
    const isArchivedParam = searchParams.get(SEARCH_PARAMS.isArchived);
    if (isEmptyList && !isLoading && !isArchivedParam && !hasTriedArchived) {
      setHasTriedArchived(true);
      onShowArchiveChange(true);
    }
  }, [isEmptyList, isLoading, searchParams, onShowArchiveChange, hasTriedArchived]);

  return (
    <ProductsView
      isEmptyList={isEmptyList}
      uploadControl={uploadControl}
      isUploadFileVisible={isUploadFileVisible}
      isMappingModalVisible={isMappingModalVisible}
      setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
      onCloseMappingModal={onCloseMappingModal}
      isImportErrorsModalVisible={isImportErrorsModalVisible}
      onDownloadExample={onDownloadExample}
      importRecords={importRecords}
      control={control}
      isArchiveModalVisible={isArchiveModalVisible}
      isArchiveModalInfoVisible={isArchiveModalInfoVisible}
      latestArchivation={latestArchivation}
      isLineMark={isLineMark}
      onSortChange={onSortChange}
      onSearchChange={onSearchChange}
      onSelectAll={onSelectAll}
      onArchiveConfirm={onArchiveConfirm}
      onArchive={onArchive}
      onArchiveModalInfoOpen={onArchiveModalInfoOpen}
      onMoveToArchive={onMoveToArchive}
      onDeArchive={onDeArchive}
      onPageChange={onPageChange}
      isArchiveSelectedItemsVisible={isArchiveSelectedItemsVisible}
      setIsArchiveModalVisible={setIsArchiveModalVisible}
      setIsArchiveModalInfoVisible={setIsArchiveModalInfoVisible}
      isActionsVisible={isActionsVisible}
      items={items}
      selectedItems={selectedItems}
      currentTotalItems={currentTotalItems}
      onEditRecord={onEditRecord}
      onAddRecord={onAddRecord}
      isProfitCenterEmpty={isProfitCenterEmpty}
      meta={meta}
      isWithSearch={isWithSearch}
      isLoading={isLoading}
      isLazyProductExampleLoading={isLazyProductExampleLoading}
      onShowArchiveChange={onShowArchiveChange}
      activeModal={activeRestrictedModal}
      closeModal={onOpenRestrictedModal}
      archiveError={archiveError as IArchiveError || deArchiveError as IArchiveError || deleteError as IArchiveError}
      onDeleteRecord={onDeleteRecord}
      isDeleteModalVisible={isDeleteModalVisible}
      onCloseDeleteModal={onCloseDeleteModal}
      onDeleteConfirm={onDeleteConfirm}
      isImportErrorModalVisible={isImportErrorModalVisible}
      importErrorMessage={importErrorMessage}
      onCloseImportErrorModal={handleCloseImportErrorModal}
      onImportError={handleImportError}
      productCategoriesOptions={productCategoriesOptions}
    />
  );
}

export default Products;
